import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Close } from '@travel/icons/ui';
import isNotEmptyArray from '@travel/ui/utils/arrays';
import useRatBindClickComponent from '@travel/traveler-core/hooks/useRatBindClickComponent';
import { MOBILE_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';

import type { Group, Filter } from 'favoriteList/constants/filters';
import {
  SORT_OPTIONS_PLAN,
  SORT_OPTIONS_PROVIDER,
  transformData,
  getSelectedFeatureIds,
  getInitFilters,
  getQueryParams,
  getSelectedFeatures,
  shortenText,
  getMemberRank,
} from 'favoriteList/constants/filters';
import FilterButton from './FilterButton/';
import { LinkWithRef } from 'favoriteList/core/universalRouter/Link/LinkWithRef';
import { getFilter } from 'favoriteList/store/providerPlan/selectors';
import { getRatId, APPEAR_SORT, CLICK_SORT } from 'favoriteList/components/DataLayer/helper';
import HorizontalNavScroll from './HorizontalNavScroll';

import styles from './filters.module.scss';

type Props = {
  // Custom callback function to handle sort and filter event
  onSearchCallback?: (sortKey: string, filter: { searchKey?: string; featureId: string }[]) => void;
  isPlanList: boolean;
  isDomestic: boolean;
  // window.location.search
  search?: string;
};

function Filters({ onSearchCallback, search, isPlanList, isDomestic }: Props) {
  const queryParams = getQueryParams(search);
  const [memberRank, setMemberRank] = useState<string>(getMemberRank());

  const filterData: Filter[] = useSelector(getFilter(memberRank));
  const staticFiltersData = transformData(filterData, styles.secondFeature, styles.icon) as Group[];

  const initFilters = getInitFilters(staticFiltersData, search);
  const sortKey = queryParams?.['f_sort']?.[0] || 'LATEST';
  const { selectedFeatures, uniqueSelectedFeatures } = getSelectedFeatures(initFilters);
  const sortOptions = isPlanList ? SORT_OPTIONS_PLAN : SORT_OPTIONS_PROVIDER;
  const isOverseasPlan = isPlanList && !isDomestic;

  const [filters, setFilters] = useState(initFilters);
  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const ratLink = useRef<HTMLAnchorElement>(null);
  const ratAppear = useRef(null);

  useEffect(() => {
    setFilters(initFilters);
  }, [filterData]);

  useRatBindClickComponent(ratLink);
  useRatBindClickComponent(ratAppear);

  const updateMemberRank = () => {
    if (memberRank === '') {
      const newMemberRank = getMemberRank();
      if (newMemberRank !== '') {
        setMemberRank(newMemberRank);
      }
    }
  };

  const onChange = (_groupId: string, featureId: string) => {
    const updatedFilters = filters.map((group) => {
      return {
        ...group,
        features: group.features?.map((feature) => {
          if (feature.featureId === featureId) {
            // if second features exists, change all seconds condition
            const newSecondFeature = isNotEmptyArray(feature.secondFeatures)
              ? feature.secondFeatures.map((secFeature) => {
                  return { ...secFeature, selected: !feature.selected };
                })
              : feature.secondFeatures;

            return {
              ...feature,
              selected: !feature.selected,
              secondFeatures: newSecondFeature,
            };
          } else {
            // if second feature is on changed
            if (feature.secondFeatures?.some((secFeature) => secFeature.featureId === featureId)) {
              const newSecondFeature = feature.secondFeatures?.map((secFeature) => {
                if (secFeature.featureId === featureId) {
                  return {
                    ...secFeature,
                    selected: !secFeature.selected,
                  };
                }
                return secFeature;
              });

              // if any second feature is true, change parent feature as true, else false
              const shouldUpdateParentStatus = newSecondFeature?.every(
                (item) => item.selected === true,
              );

              return {
                ...feature,
                selected: shouldUpdateParentStatus,
                secondFeatures: newSecondFeature,
              };
            } else {
              return feature;
            }
          }
        }),
      };
    });

    setFilters(updatedFilters);
  };

  const onSearch = () => {
    const requestFilters = getSelectedFeatureIds(filters);
    onSearchCallback?.(sortKey, requestFilters);
  };

  const onClear = () => {
    const clearFilters = filters.map((group) => ({
      ...group,
      features: group.features?.map((feature) => ({
        ...feature,
        selected: false,
        secondFeatures: feature.secondFeatures?.map((secFeature) => ({
          ...secFeature,
          selected: false,
        })),
      })),
    }));

    setFilters(clearFilters);
  };

  const onRemoveTag = (featureId: string) => {
    const removeFilters = initFilters.map((group) => ({
      ...group,
      features: group.features?.map((feature) => {
        if (feature.secondFeatures?.some((secFeature) => secFeature.featureId === featureId)) {
          const newSecondFeature = feature.secondFeatures?.map((secFeature) => ({
            ...secFeature,
            selected: featureId === secFeature.featureId ? false : secFeature.selected,
          }));

          const shouldUpdateParentStatus = newSecondFeature?.every(
            (item) => item.selected === true,
          );

          return {
            ...feature,
            selected: shouldUpdateParentStatus,
            secondFeatures: newSecondFeature,
          };
        } else {
          return {
            ...feature,
            selected: featureId === feature.featureId ? false : feature.selected,
          };
        }
      }),
    }));

    const requestFilters = getSelectedFeatureIds(removeFilters);

    onSearchCallback?.(sortKey, requestFilters);
  };

  const onChangeSortKey = (event: any) => {
    const sortKey = event.target.value || 'LATEST';
    const requestFilters = getSelectedFeatureIds(initFilters);

    onSearchCallback?.(sortKey, requestFilters);
  };

  const onClickSortKey = () => {
    ratLink.current?.click();
  };

  const renderSortOptions = Object.values(sortOptions).map((option) => ({
    text: option.labelId,
    value: option.value,
  }));

  const filterTags = uniqueSelectedFeatures.map((item) => {
    return (
      <div key={item.featureId} data-testid="filters-tag" className={styles.filterTag}>
        <div>{shortenText(item.name, item.noCutShortOver)}</div>
        <button
          type="button"
          className={styles.removeButton}
          onClick={() => onRemoveTag?.(item.featureId)}
          aria-label="remove tag"
          data-testid="filters-removeTag-button"
        >
          <Close size={16} color="lincolnGreen" />
        </button>
      </div>
    );
  });

  return (
    <div className={styles.wrapper} data-testid="filters-wrapper">
      {!isOverseasPlan && (
        <FilterButton
          filters={filters}
          isFeatureSelected={isNotEmptyArray(selectedFeatures)}
          onSearch={onSearch}
          onClear={onClear}
          onChange={onChange}
          isPlanList={isPlanList}
          updateMemberRank={updateMemberRank}
        />
      )}

      <div className={styles.optionsWrapper}>
        <div className={styles.sortKeyWrapper}>
          <select
            name="sortKey"
            id="sortKey"
            className={styles.selectBoxWrapper}
            onBlur={() => {}}
            onChange={onChangeSortKey}
            onClick={onClickSortKey}
            data-testid="filters-sortKey-select"
            data-ratevent="appear"
            data-ratParam="all"
            data-ratid={getRatId(APPEAR_SORT, isPlanList, isMobile)}
            ref={ratAppear}
          >
            {renderSortOptions.map((item) => {
              return (
                <option
                  value={item.value}
                  className={styles.sortKeyOption}
                  key={item.value}
                  selected={item.value === sortKey}
                >
                  {item.text}
                </option>
              );
            })}
          </select>
        </div>

        {isMobile ? filterTags : <HorizontalNavScroll>{filterTags}</HorizontalNavScroll>}
      </div>

      <LinkWithRef
        data-ratevent="click"
        data-ratParam="all"
        data-ratid={getRatId(CLICK_SORT, isPlanList, isMobile)}
        ref={ratLink}
        to={''}
      >
        {null}
      </LinkWithRef>
    </div>
  );
}

export default Filters;
